import _objectDestructuringEmpty from "@babel/runtime/helpers/esm/objectDestructuringEmpty";
import get from 'transmute/get';
import { useCallback, useEffect, useMemo, useRef, useState, useContext, Fragment, memo } from 'react';
import { useSelector } from 'react-redux';
import { getAttachments, getCommonMessage, getVid } from 'conversations-thread-data/common-reply/public/operators/commonReplyGetters';
import { getReplyToSpecificMessages } from 'conversations-thread-data/channel-configuration/public/operators';
import { getAuthAsPojo, getIsWhatsApp, getIsUngatedForConversationSummary, getIsUngatedForRichTextFormattingTray } from 'conversations-thread-data/auth/public/selectors';
// TODO: move to composer-ui
import { ChannelCapabilitiesConfigContext } from 'conversations-thread-view/channel-capabilities/context/ChannelCapabilitiesConfigContext';
import { getIsForward
// @ts-expect-error module not typed
} from 'conversations-message-history/common-message-format/operators/commonMessageFormatGetters';
import { getAttachmentInsertFailed } from '../../../usage-tracking/events/editorInteractions';
import { getMessageShouldIncludePreviousHistory } from 'conversations-thread-data/message-publishing/public/operators';
import { addAttachmentToCommonReply } from 'conversations-thread-data/common-reply/public/operators/addAttachmentToCommonReply';
import { removeAttachmentFromCommonReply } from 'conversations-thread-data/common-reply/public/operators/removeAttachmentFromCommonReply';
import { useAsyncCapabilitiesConfig } from 'conversations-thread-data/channel-capabilities/public/hooks';
import { WHATSAPP_GENERIC_CHANNEL_ID } from 'conversations-thread-data/generic-channels/public/constants';
import { useEditorCapabilities } from '../hooks/useEditorCapabilities';
import AttachmentErrorModal from './AttachmentErrorModal';
import EditorStateProvider from './EditorStateProvider';
import PreviousMessageHistoryWrapper from './PreviousMessageHistoryWrapper';
import { useAttachmentAndFileUploads } from '../../../attachments/protected/hooks';
import { AttachmentTray } from '../../../attachments/protected/components';
import { useProsemirrorConfigFromCapabilities, useRenderCounter } from '../../../prosemirror/protected/hooks';
import { EditorTrackerContext } from '../../../usage-tracking/context/EditorTrackerContext';
import { useDisableEditor } from 'composer-data/disable-editor/public/hooks';
import DisabledEditor from './disabled-editor/DisabledEditor';
import Raven from 'raven-js';
import { Flex } from 'foundations-components/components/Flex';
import { MacrosTray } from '../../../macros/MacrosTray';
import { THREAD_COMMENT } from 'conversations-message-history/comment-message/constants/messageTypes';
import useRenderAnalyzer from '../hooks/useRenderAnalyzer';
import { localStorage } from 'conversations-platform/localStorage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const RichTextEditorProvider = props => {
  var _macrosMenuConfig$onM;
  const {
    className,
    contactEmail,
    contactVid,
    getDisabledProps,
    inboxId,
    maxSize,
    minHeight,
    onChange,
    onCloseShepherd,
    onFocus,
    onInsert,
    onSubmit,
    onTyping,
    originalChannelInstanceId,
    placeholder,
    renderExistingFileSelect,
    renderSubmitButton,
    reply,
    replyRecommendationsEnabled,
    threadId,
    savePersistentDraft,
    clearPersistentDraft,
    draftSaveFailed,
    macrosMenuConfig,
    handleFirstClick
  } = props;
  const [richTextEditorPresent, setRichTextEditorPresent] = useState(false);
  const [selectedMacro, setSelectedMacro] = useState(null);
  const onMacroSelection = (_macrosMenuConfig$onM = macrosMenuConfig === null || macrosMenuConfig === void 0 ? void 0 : macrosMenuConfig.onMacroSelection) !== null && _macrosMenuConfig$onM !== void 0 ? _macrosMenuConfig$onM : null;
  const auth = useSelector(getAuthAsPojo);
  const {
    capabilitiesBlob,
    genericChannelId,
    originalGenericChannelId,
    latestCMFGenericChannelId
  } = useContext(ChannelCapabilitiesConfigContext);
  const isUngatedForWhatsApp = useSelector(getIsWhatsApp);
  const isUngatedForConversationSummary = useSelector(getIsUngatedForConversationSummary) &&
  // TODO - Remove when we get legal approval for WA
  originalGenericChannelId !== WHATSAPP_GENERIC_CHANNEL_ID;
  const isUngatedForRichTextFormattingTray = useSelector(getIsUngatedForRichTextFormattingTray);
  const message = getCommonMessage(reply);
  const recipientId = getVid(reply);
  const {
    reason,
    shouldDisableEditor,
    messageLimitData,
    newestThreadId
  } = useDisableEditor({
    threadId,
    currentEditorKey: genericChannelId,
    gates: {
      isUngatedForWhatsApp
    },
    commonMessage: message,
    contactVid: recipientId,
    originalChannelInstanceId
  });
  const shouldRenderDisabledEditor = shouldDisableEditor && isUngatedForWhatsApp;
  const attachmentModalRef = useRef(null);
  const setRteRefCallback = useCallback(node => setRichTextEditorPresent(Boolean(node)), [setRichTextEditorPresent]);
  const isForwardingMessage = getIsForward(message);
  const containerClass = `previous-message-history-container-${inboxId}`;
  const canReplyToSpecificMessages = getReplyToSpecificMessages(capabilitiesBlob);
  const canShowHistory = useMemo(() => {
    return getMessageShouldIncludePreviousHistory({
      canReplyToSpecificMessages,
      isForwardingMessage,
      latestCMFGenericChannelId: latestCMFGenericChannelId,
      originalGenericChannelId: originalGenericChannelId
    }) && richTextEditorPresent;
  }, [canReplyToSpecificMessages, isForwardingMessage, latestCMFGenericChannelId, originalGenericChannelId, richTextEditorPresent]);
  const capabilitiesConfig = useEditorCapabilities({
    renderExistingFileSelect,
    isUngatedForConversationSummary,
    threadId
  });
  const {
    trackEvent
  } = useContext(EditorTrackerContext);

  /* File attachment changes through plugins */
  const handleAttachmentChange = useCallback(attachment => {
    const updatedReply = addAttachmentToCommonReply({
      attachment,
      commonReply: reply
    });
    onChange(updatedReply);
  }, [reply, onChange]);
  const removeUploadedFile = useCallback(uploadedFile => {
    const fileId = get('id', uploadedFile);
    if (fileId) {
      const updatedReply = removeAttachmentFromCommonReply({
        fileId,
        commonReply: reply
      });
      onChange(updatedReply);
    }
  }, [reply, onChange]);

  // Get the file upload state and handlers to manage it.
  const onDropFileUpload = handleAttachmentChange;
  const onDropFileUploadFailed = useCallback((__error, attachment) => {
    trackEvent(getAttachmentInsertFailed({
      file: attachment
    }));
  }, [trackEvent]);
  const {
    indexedAsyncFileUploads,
    removeFileUpload,
    deleteFileUpload,
    uploadFile,
    maxFileLimit,
    maxTotalFileSizeLimit
  } = useAttachmentAndFileUploads({
    threadId,
    onUpload: onDropFileUpload,
    onError: onDropFileUploadFailed,
    capabilitiesConfig
  });
  const handleDeleteFileUpload = useCallback(({
    fileUpload
  }) => {
    const fileId = fileUpload && fileUpload.get('id');
    if (fileId) {
      deleteFileUpload({
        variables: {
          fileIds: [fileId.toString()]
        }
      }).catch(error => {
        Raven.captureException(error);
      });
    }
  }, [deleteFileUpload]);
  const attachments = getAttachments(reply);
  const attachmentsSize = attachments.size;
  const fileUploadsInProgCount = indexedAsyncFileUploads.size;
  const dynamicFileUploadSizeRef = useRef(attachmentsSize + fileUploadsInProgCount);

  // Debug hook to analyze prop/state changes
  useRenderAnalyzer('RichTextEditorProvider', !!localStorage.getItem('EDITOR_SHIELD_DEBUG'), {
    props,
    selectors: {
      auth,
      isUngatedForWhatsApp,
      isUngatedForConversationSummary
    },
    contexts: {
      capabilitiesBlob,
      genericChannelId,
      originalGenericChannelId,
      latestCMFGenericChannelId,
      trackEvent
    }
  });
  const getDisabledPropsCallback = useCallback(_ref => {
    let args = Object.assign({}, (_objectDestructuringEmpty(_ref), _ref));
    return getDisabledProps(Object.assign({
      indexedAsyncFileUploads,
      maxFileLimit,
      attachments,
      maxTotalFileSizeLimit
    }, args));
  }, [getDisabledProps, indexedAsyncFileUploads, maxFileLimit, attachments, maxTotalFileSizeLimit]);
  const handleRemoveMacro = useCallback(() => {
    setSelectedMacro(null);
    onMacroSelection === null || onMacroSelection === void 0 || onMacroSelection(null);
  }, [onMacroSelection]);
  const handleSelectMacro = useCallback(macro => {
    setSelectedMacro(macro);
    onMacroSelection === null || onMacroSelection === void 0 || onMacroSelection(macro);
  }, [onMacroSelection]);
  useEffect(() => {
    if (genericChannelId === THREAD_COMMENT) {
      handleRemoveMacro();
    }
  }, [genericChannelId, handleRemoveMacro]);
  const sendAndClearMacro = useCallback(params => {
    setSelectedMacro(null);
    onMacroSelection === null || onMacroSelection === void 0 || onMacroSelection(null);
    onSubmit(params);
  }, [onSubmit, onMacroSelection]);
  const renderMacroSubmitButton = useCallback(params => {
    return renderSubmitButton(Object.assign({}, params, {
      macroSelected: Boolean(selectedMacro)
    }));
  }, [renderSubmitButton, selectedMacro]);
  const renderTray = useCallback(() => {
    return /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      id: "tray-container",
      children: [/*#__PURE__*/_jsx(AttachmentTray, {
        attachments: attachments,
        indexedAsyncFileUploads: indexedAsyncFileUploads,
        maxFileLimit: maxFileLimit,
        maxTotalFileSizeLimit: maxTotalFileSizeLimit,
        onCancelInProgressFileUpload: removeFileUpload,
        onDeleteFileUpload: handleDeleteFileUpload,
        onRemoveUploadedFile: removeUploadedFile
      }), /*#__PURE__*/_jsx(MacrosTray, {
        selectedMacro: selectedMacro,
        onRemoveMacro: handleRemoveMacro
      })]
    });
  }, [attachments, maxFileLimit, removeFileUpload, removeUploadedFile, maxTotalFileSizeLimit, indexedAsyncFileUploads, handleDeleteFileUpload, selectedMacro, handleRemoveMacro]);
  useEffect(() => {
    dynamicFileUploadSizeRef.current = fileUploadsInProgCount + attachmentsSize;
  }, [fileUploadsInProgCount, attachmentsSize]);
  const uploadDroppedFiles = useCallback(files => {
    if (files.length + dynamicFileUploadSizeRef.current > maxFileLimit && attachmentModalRef.current) {
      attachmentModalRef.current.openModal();
      return;
    }
    files.forEach(file => {
      uploadFile(file);
    });
  }, [dynamicFileUploadSizeRef, maxFileLimit, uploadFile, attachmentModalRef]);
  const {
    prosemirrorConfig,
    loading: isConfigLoading
  } = useProsemirrorConfigFromCapabilities({
    renderExistingFileSelect,
    fileAttachmentProps: {
      attachments,
      threadId,
      handleAttachmentChange,
      fileUploadsInProgCount,
      uploadFile,
      uploadDroppedFiles
    },
    aiAssistantProps: {
      replyRecommendationsEnabled: !!replyRecommendationsEnabled,
      threadId
    },
    draftSaveFailed,
    macrosProps: {
      enableMacros: Boolean(macrosMenuConfig) && genericChannelId !== THREAD_COMMENT,
      macrosMenuConfig,
      handleSelectMacro
    },
    useFormattingTray: isUngatedForRichTextFormattingTray
  });

  // Hydrate capabilities config with any BE data required
  // to run the editor.
  const asyncCapabilitiesConfig = useAsyncCapabilitiesConfig(capabilitiesConfig, inboxId, threadId, contactVid, contactEmail, auth);
  useRenderCounter();
  const rte =
  /*#__PURE__*/
  // @bend-expect trellis-tokenized
  _jsx(EditorStateProvider, {
    auth: auth,
    asyncCapabilitiesConfig: asyncCapabilitiesConfig,
    prosemirrorConfig: prosemirrorConfig,
    attachments: attachments,
    className: className,
    getDisabledProps: getDisabledPropsCallback,
    fileUploadsInProgCount: fileUploadsInProgCount,
    handleAttachmentChange: handleAttachmentChange,
    isConfigLoading: isConfigLoading,
    originalGenericChannelId: originalGenericChannelId,
    maxSize: maxSize,
    minHeight: minHeight,
    onChange: onChange,
    onCloseShepherd: onCloseShepherd,
    onFocus: onFocus,
    onInsert: onInsert,
    onSubmit: sendAndClearMacro,
    onTyping: onTyping,
    placeholder: placeholder,
    renderSubmitButton: renderMacroSubmitButton,
    renderTray: renderTray,
    reply: reply,
    replyRecommendationsEnabled: replyRecommendationsEnabled,
    threadId: threadId,
    setRteRefCallback: setRteRefCallback,
    uploadDroppedFiles: uploadDroppedFiles,
    uploadFile: uploadFile,
    savePersistentDraft: savePersistentDraft,
    clearPersistentDraft: clearPersistentDraft,
    handleFirstClick: handleFirstClick
  });

  // upload images as attachments if we don't have image capabiltiies

  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(AttachmentErrorModal, {
      maxFileLimit: maxFileLimit,
      attachmentsCount: attachmentsSize,
      fileUploadsInProgCount: fileUploadsInProgCount,
      ref: attachmentModalRef
    }), !shouldRenderDisabledEditor && rte, shouldRenderDisabledEditor && /*#__PURE__*/_jsx(DisabledEditor, {
      currentEditorKey: genericChannelId,
      recipientId: recipientId,
      message: message,
      threadId: threadId,
      newestThreadId: newestThreadId,
      reason: reason,
      messageLimitData: messageLimitData || undefined
    }), canShowHistory && /*#__PURE__*/_jsx(PreviousMessageHistoryWrapper, {
      threadId: threadId,
      containerClass: containerClass
    })]
  });
};
RichTextEditorProvider.displayName = 'RichTextEditorProvider';
export default /*#__PURE__*/memo(RichTextEditorProvider);